/**
 * EE AI 使用記錄頁（Phase 5）
 * func_code: ee_ai_usage
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stack, Tabs, Tab, Button, Chip, Paper, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Typography, Divider, Card, CardContent, Grid, Collapse,
  IconButton, Tooltip, Pagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, BarChart as BarChartIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import { logView } from '../../utils/userLogHelper';
import { usePermission } from '../../hooks/usePermission';
import {
  AiUsageLog, AiUsageStats, PaginatedUsageLogs,
  listAiUsageLogs, getAiUsageStats,
} from '../../services/eeAiService';
import { formatLocal } from '../../utils/dateHelper';

const FUNC_CODE = 'ee_ai_usage';

const fmt = (iso?: string | null) =>
  iso ? formatLocal(iso) : '—';

const fmtNum = (n?: number | null) =>
  n != null ? n.toLocaleString() : '—';

// ============================================================
// Tab 1：統計面板
// ============================================================
const StatsTab: React.FC = () => {
  const { showApiError } = useMessage();
  const [stats, setStats] = useState<AiUsageStats | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getAiUsageStats(days);
      setStats(s);
      logView(FUNC_CODE, { tab: 'stats', days, total_calls: s.total_calls });
    } catch (err: any) {
      logView(FUNC_CODE, { tab: 'stats', days }, String(err?.message || err));
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [days, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>統計區間</InputLabel>
          <Select
            label="統計區間"
            value={days}
            onChange={e => setDays(Number(e.target.value))}
          >
            {[7, 14, 30, 90, 180, 365].map(d => (
              <MenuItem key={d} value={d}>最近 {d} 天</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small" variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={reload} disabled={loading}
        >
          重新整理
        </Button>
      </Stack>

      {stats && (
        <Stack spacing={3}>
          {/* 總覽卡片 */}
          <Grid container spacing={2}>
            {[
              { label: '總呼叫次數', value: fmtNum(stats.total_calls), color: 'primary.main' },
              { label: '成功次數', value: fmtNum(stats.success_calls), color: 'success.main' },
              { label: '失敗次數', value: fmtNum(stats.failed_calls), color: 'error.main' },
              {
                label: '成功率',
                value: stats.total_calls > 0
                  ? `${(stats.success_rate * 100).toFixed(1)}%`
                  : '—',
                color: 'text.primary',
              },
              { label: '總 Tokens', value: fmtNum(stats.total_tokens), color: 'text.primary' },
              {
                label: '平均回應時間',
                value: stats.avg_duration_ms != null
                  ? `${(stats.avg_duration_ms / 1000).toFixed(1)} 秒`
                  : '—',
                color: 'text.primary',
              },
            ].map(({ label, value, color }) => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={label}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {label}
                    </Typography>
                    <Typography variant="h6" sx={{ color, fontWeight: 600, mt: 0.5 }}>
                      {value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider />

          <Grid container spacing={2}>
            {/* 依模型分布 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BarChartIcon fontSize="small" /> 依模型分布
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>模型</TableCell>
                      <TableCell align="right">次數</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.by_model.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                          無資料
                        </TableCell>
                      </TableRow>
                    ) : (
                      stats.by_model.map(m => (
                        <TableRow key={m.model_name} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {m.model_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{m.calls.toLocaleString()}</TableCell>
                          <TableCell align="right">{m.tokens.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* 依呼叫來源分布 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BarChartIcon fontSize="small" /> 依呼叫來源
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>來源類型</TableCell>
                      <TableCell align="right">次數</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.by_caller_type.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                          無資料
                        </TableCell>
                      </TableRow>
                    ) : (
                      stats.by_caller_type.map(c => (
                        <TableRow key={c.caller_type} hover>
                          <TableCell>
                            <Chip label={c.caller_type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{c.calls.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Stack>
      )}

      {!stats && !loading && (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          無統計資料
        </Typography>
      )}
    </Box>
  );
};

// ============================================================
// 展開列（顯示 prompt_preview / error_message）
// ============================================================
const ExpandedRow: React.FC<{ log: AiUsageLog }> = ({ log }) => (
  <Box sx={{ px: 3, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
    {log.prompt_preview && (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block">Prompt 預覽</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
          {log.prompt_preview}
        </Typography>
      </Box>
    )}
    {log.error_message && (
      <Box>
        <Typography variant="caption" color="error" display="block">錯誤訊息</Typography>
        <Typography variant="body2" color="error.main" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {log.error_message}
        </Typography>
      </Box>
    )}
    {!log.prompt_preview && !log.error_message && (
      <Typography variant="caption" color="text.secondary">無詳細資料</Typography>
    )}
  </Box>
);

// ============================================================
// Tab 2：詳細記錄
// ============================================================
const LogsTab: React.FC = () => {
  const { showApiError } = useMessage();
  const [result, setResult] = useState<PaginatedUsageLogs | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [callerType, setCallerType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const reload = useCallback(async () => {
    setLoading(true);
    const filters = {
      tab: 'logs',
      caller_type: callerType || undefined,
      status: statusFilter || undefined,
      page,
    };
    try {
      const r = await listAiUsageLogs({
        caller_type: callerType || undefined,
        status: statusFilter || undefined,
        page,
        page_size: pageSize,
      });
      setResult(r);
      logView(FUNC_CODE, { ...filters, total: r.total });
    } catch (err: any) {
      logView(FUNC_CODE, filters, String(err?.message || err));
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [callerType, statusFilter, page, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const handleFilter = () => { setPage(1); reload(); };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>呼叫來源</InputLabel>
          <Select label="呼叫來源" value={callerType}
                  onChange={e => setCallerType(e.target.value)}>
            <MenuItem value="">全部</MenuItem>
            <MenuItem value="manual">manual</MenuItem>
            <MenuItem value="scheduler">scheduler</MenuItem>
            <MenuItem value="api_service">api_service</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>狀態</InputLabel>
          <Select label="狀態" value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">全部</MenuItem>
            <MenuItem value="success">成功</MenuItem>
            <MenuItem value="failed">失敗</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" onClick={handleFilter}>
          套用篩選
        </Button>
        <Button
          size="small" variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={reload} disabled={loading}
        >
          重新整理
        </Button>
        {result && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            共 {result.total.toLocaleString()} 筆
          </Typography>
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width={40} />
              <TableCell>時間</TableCell>
              <TableCell width={100}>Provider</TableCell>
              <TableCell width={120}>模型</TableCell>
              <TableCell width={100}>來源</TableCell>
              <TableCell width={80} align="right">Tokens</TableCell>
              <TableCell width={80} align="right">耗時</TableCell>
              <TableCell width={70}>狀態</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!result || result.items.length === 0) ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '載入中…' : '無記錄'}
                </TableCell>
              </TableRow>
            ) : (
              result.items.map(log => (
                <React.Fragment key={log.id}>
                  <TableRow hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                    <TableCell>
                      <IconButton size="small">
                        {expanded === log.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmt(log.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {log.provider_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {log.model_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Chip label={log.caller_type} size="small" variant="outlined" />
                        {log.caller_ref && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {log.caller_ref}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {fmtNum(log.total_tokens)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {log.duration_ms != null ? `${(log.duration_ms / 1000).toFixed(1)}s` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.status === 'success' ? '成功' : '失敗'}
                        size="small"
                        color={log.status === 'success' ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  {expanded === log.id && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0 }}>
                        <ExpandedRow log={log} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {result && result.total > pageSize && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={Math.ceil(result.total / pageSize)}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            color="primary"
          />
        </Stack>
      )}
    </Box>
  );
};

// ============================================================
// 主頁面
// ============================================================
const EeAiUsagePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const { hasPermission, loading: permissionLoading } = usePermission();

  if (permissionLoading) return null;
  if (!hasPermission(FUNC_CODE, 'read')) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">無權限存取此頁面</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FunctionPageHeader funcCode="ee_ai_usage" fallback="AI 使用記錄" />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="統計面板" />
        <Tab label="詳細記錄" />
      </Tabs>

      <Box sx={{ mt: 2 }}>
        {tab === 0 && <StatsTab />}
        {tab === 1 && <LogsTab />}
      </Box>
    </Box>
  );
};

export default EeAiUsagePage;